import { useTranslation } from 'react-i18next';
import { Alert, Box, useTheme } from '@mui/material';
import { powerProfileModes } from 'greener-ui-library';
import { conventionalGensetFactor } from '../utils/constants';
import { getPeakPowerFromCsv } from '../utils/powerProfileCsvConverter';

interface ConventionalGensetValidationAlertProps {
  formData: {
    powerProfileMode: string;
    peakPowerKw?: number;
    editedPowerProfileCsv?: string;
    rawPowerProfileCsv?: string;
    gensetKva: number | string;
    gensetCount: number | string;
  };
  // Configurable factor (default from constants, can be overridden)
  factor?: number;
  // Responsive width constraints (matching ResponsiveFormContainer)
  maxColumnWidth?: number;
  minColumnWidth?: number;
  // Gap between columns (matching ResponsiveFormContainer gap prop)
  gap?: number;
}

export const ConventionalGensetValidationAlert = ({
  formData,
  factor = conventionalGensetFactor,
  maxColumnWidth,
  minColumnWidth,
  gap = 2,
}: ConventionalGensetValidationAlertProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Calculate max power peak from different sources
  let maxPowerPeak: number | undefined;

  if (formData.powerProfileMode === powerProfileModes.file) {
    // File mode: get peak from CSV
    const csvData = formData.editedPowerProfileCsv || formData.rawPowerProfileCsv;
    if (csvData) {
      const peakFromCsv = getPeakPowerFromCsv(csvData);
      maxPowerPeak = peakFromCsv ? Math.ceil(peakFromCsv) : undefined;
    }
  } else if (formData.powerProfileMode === powerProfileModes.manual) {
    // Manual mode: if chart was edited, use peak from edited CSV, otherwise use peakPowerKw
    const csvData = formData.editedPowerProfileCsv || formData.rawPowerProfileCsv;
    if (csvData) {
      const peakFromCsv = getPeakPowerFromCsv(csvData);
      maxPowerPeak = peakFromCsv ? Math.ceil(peakFromCsv) : undefined;
    } else {
      // Fallback to peakPowerKw if no CSV data
      maxPowerPeak = formData.peakPowerKw ? Math.ceil(formData.peakPowerKw) : undefined;
    }
  }

  // If we don't have maxPowerPeak yet, don't show alert
  if (!maxPowerPeak) {
    return null;
  }

  // Parse genset values
  const gensetKva = typeof formData.gensetKva === 'string' ? parseFloat(formData.gensetKva) : formData.gensetKva;
  const gensetCount =
    typeof formData.gensetCount === 'string' ? parseFloat(formData.gensetCount) : formData.gensetCount;

  // Check if values are valid numbers
  if (!gensetKva || isNaN(gensetKva) || !gensetCount || isNaN(gensetCount)) {
    return null;
  }

  // Calculate total genset capacity
  const totalGensetKva = gensetKva * gensetCount;

  // Calculate required capacity (factor × maxPowerPeak)
  const requiredKva = factor * maxPowerPeak;

  // Check if under-dimensioned
  if (totalGensetKva < requiredKva) {
    return (
      <Box
        sx={{
          mb: 3,
          mt: 2.5,
          width: '100%',
          ...(maxColumnWidth && minColumnWidth
            ? {
                maxWidth: {
                  xs: '100%',
                  sm: `calc(${maxColumnWidth * 2}px + ${theme.spacing(gap)})`, // 2 columns + gap
                },
                [theme.breakpoints.down('lg')]: {
                  maxWidth: '100%',
                },
              }
            : {}),
        }}
      >
        <Alert
          severity="error"
          sx={{
            width: '100%',
            ...(maxColumnWidth && minColumnWidth
              ? {
                  maxWidth: {
                    xs: '100%',
                    sm: `calc(${maxColumnWidth * 2}px + ${theme.spacing(gap)})`, // 2 columns + gap
                  },
                  [theme.breakpoints.down('lg')]: {
                    maxWidth: '100%',
                  },
                }
              : {}),
            '& .MuiAlert-icon': {
              alignItems: 'center',
              display: 'flex',
            },
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: t('blueprints:wizard.validations.conventionalGensetUnderDimensioned', {
                required: requiredKva.toFixed(1),
                maxPowerPeak: maxPowerPeak.toFixed(1),
                factor: factor.toFixed(1),
              }),
            }}
          />
        </Alert>
      </Box>
    );
  }

  return null;
};
